import { useEffect, useState } from "react";
import type { AgentDTO, SkillDetailDTO, SkillDTO, SkillLevel } from "../../../shared/types";
import { formatBytes } from "../../components/Files";
import { Dialog } from "../../components/Dialog";
import { SkillDetail } from "../../components/SkillDetail";
import { ImportSkillForm, NewSkillForm } from "../../components/SkillForm";
import { LEVEL_ICON, LevelTag, SkillWho } from "../../components/SkillWho";
import { Table } from "../../components/Table";
import { Tabs } from "../../components/Tabs";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, ErrorText, inputCls, PageHeader, timeAgo } from "../../components/ui";
import { get } from "../../lib/api";
import { navigate, useLocation } from "../../lib/router";
import { Switch } from "../../components/Switch";

type Creating = "new" | "import" | null;

/** The three levels, in the order a new skill's form offers them: each is a tab, with what it means under the tabs. */
const LEVELS: { key: SkillLevel; label: string; hint: string }[] = [
  { key: "agent", label: "Agent", hint: "The agents' own skills, whoever the agent works for." },
  { key: "scope", label: "Scope", hint: "One scope's skills, for every agent working under that scope." },
  { key: "person", label: "Person", hint: "One person's skills, on the agents they share them with." },
];
const LEVEL_ORDER: Record<SkillLevel, number> = { agent: 0, scope: 1, person: 2 };

/**
 * Build → Skills: the procedures agents follow, one tab per level (where they apply) and All. A run is told only a skill's one-line
 * description and gets the text through `pandas skill read`, which is also how every load is counted.
 */
export function AdminSkills({ skillId }: { skillId?: string }) {
  const [skills, setSkills] = useState<SkillDTO[]>([]);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const { search } = useLocation();
  const tab = LEVELS.some((l) => l.key === search.get("level")) ? (search.get("level") as SkillLevel) : null;
  /** The tab lives in the address, so a link opens it; switching replaces it rather than stacking history. */
  const openTab = (key: SkillLevel | null) => navigate(key ? `/admin/skills?level=${key}` : "/admin/skills", true);
  const [agentId, setAgentId] = useState("");
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState<Creating>(null);
  const [dropped, setDropped] = useState<string[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  /** A new skill closes the form and opens its own popup, never both at once, carrying what its header lost. */
  const created = (skill: SkillDetailDTO) => {
    setCreating(null);
    setDropped(skill.ignoredHeaderKeys);
    navigate(`/admin/skills/${skill.id}`);
  };

  // Every level is fetched so each tab can show its count; the tab only filters what is drawn.
  const query = `/api/skills?${new URLSearchParams({ ...(agentId ? { agentId } : {}), ...(q ? { q } : {}), ...(archived ? { archived: "1" } : {}) })}`;
  const load = () => get<SkillDTO[]>(query).then(setSkills, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [query]);
  useEffect(() => {
    get<AgentDTO[]>("/api/agents").then(setAgents, (e) => setError(e.message));
  }, []);

  const count = (key: SkillLevel) => skills.filter((s) => s.level === key).length;
  const shown = tab ? skills.filter((s) => s.level === tab) : [...skills].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || a.name.localeCompare(b.name));
  const hint = tab ? LEVELS.find((l) => l.key === tab)!.hint : "Every skill, whatever its level.";

  return (
    <div>
      <PageHeader
        title="Skills"
        subtitle="A skill is a procedure an agent follows: SKILL.md plus any scripts, templates and reference files. Three levels — one person's (shared with the agents they choose), one scope's, and the agents' own. A run is told the one-line descriptions of the skills it is offered and loads one with pandas skill read, which is where every load is counted."
        actions={
          <>
            <Button onClick={() => setCreating("import")}>Import a folder</Button>
            <Button variant="primary" onClick={() => setCreating("new")}>New skill</Button>
          </>
        }
      />
      <div className="mt-6 flex flex-col gap-1.5 px-4 md:px-6">
        <Tabs
          label="Level"
          value={tab ?? "all"}
          onChange={(key) => openTab(key === "all" ? null : key)}
          tabs={[{ key: "all" as const, label: "All", count: skills.length }, ...LEVELS.map((l) => ({ key: l.key, label: l.label, icon: LEVEL_ICON[l.key], count: count(l.key) }))]}
        />
        <p className="mb-4 px-1 text-xs text-muted">{hint}</p>
      </div>
      <div className="flex flex-col gap-2 px-4 sm:flex-row sm:items-center md:px-6">
        <select className={`${inputCls} sm:w-52`} value={agentId} onChange={(e) => setAgentId(e.target.value)} aria-label="Agent">
          <option value="">Every agent</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <input className={`${inputCls} sm:w-72`} value={q} placeholder="Search name or description" onChange={(e) => setQ(e.target.value)} />
        <Switch size="sm" className="shrink-0 text-muted" label="Show archived" checked={archived} onChange={setArchived} />
      </div>
      <div className="p-4 md:p-6">
        <ErrorText error={error} />
        <Table
          rows={shown}
          rowKey={(s) => s.id}
          onRowClick={(s) => navigate(`/admin/skills/${s.id}${tab ? `?level=${tab}` : ""}`)}
          empty={
            <>
              No {tab ? `${tab} ` : ""}skills{agentId || q ? " match" : " yet"}. <TextLink onClick={() => setCreating("new")}>Write one</TextLink> or <TextLink onClick={() => setCreating("import")}>import a folder</TextLink>.
            </>
          }
          columns={[
            {
              header: "Skill",
              primary: true,
              cell: (s) => (
                <>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-sm">{s.name}</span>
                    <Badge>v{s.version}</Badge>
                    <LevelTag level={s.level} />
                    {s.archivedAt && <Badge tone="amber">archived</Badge>}
                    {!s.archivedAt && !s.offered && (
                      <span title="No agent has this skill, so no run is offered it. Give it to one under Used by.">
                        <Badge tone="amber">not offered</Badge>
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-sm text-muted">{s.description}</div>
                </>
              ),
            },
            { header: "Who has it", cell: (s) => <SkillWho skill={s} agents={agents} /> },
            { header: "Files", className: "text-xs text-muted", cell: (s) => `${s.fileCount} ${s.fileCount === 1 ? "file" : "files"} · ${formatBytes(s.size)}` },
            {
              header: "Loads",
              className: "text-xs text-muted",
              cell: (s) => (
                <span title={s.lastReadAt ? `last loaded ${timeAgo(s.lastReadAt)}` : "never loaded"}>
                  {s.reads} {s.reads === 1 ? "load" : "loads"}{s.lastReadAt ? ` · ${timeAgo(s.lastReadAt)}` : ""}
                </span>
              ),
            },
            { header: "Updated", className: "text-xs text-muted", cell: (s) => timeAgo(s.updatedAt) },
          ]}
        />
      </div>
      <Dialog open={creating !== null} onClose={() => setCreating(null)} size="lg" title={creating === "import" ? "Import a skill folder" : "New skill"}>
        {creating === "import" ? (
          <ImportSkillForm agents={agents} onCreated={created} />
        ) : (
          <NewSkillForm agents={agents} onCreated={created} />
        )}
      </Dialog>
      {skillId && (
        <SkillDetail
          skillId={skillId}
          agents={agents}
          dropped={dropped}
          onClose={() => {
            setDropped(undefined);
            navigate(tab ? `/admin/skills?level=${tab}` : "/admin/skills");
          }}
          onChanged={load}
        />
      )}
    </div>
  );
}
